import {  type FC, useEffect, useRef } from 'react';
import cx from 'classnames';
import { Input } from 'antd';
import { Button } from '@g2u/ui-kit';
import { observer } from 'mobx-react-lite';

import { BookingStore, RootStore } from 'store';
import { type TextAreaRef } from 'antd/es/input/TextArea';
import styles from './notepad-content.module.scss';

const InputSection: FC<{ idx: number }> = observer((props) => {

  const { idx } = props;
  const { notepad, setNotepad } = RootStore;
  const { notes, setNotes } = BookingStore;
  const titleRef = useRef<TextAreaRef>(null);
  const descriptionRef = useRef<TextAreaRef>(null);

  const note = notepad[idx] || { title: '', description: '' };


  useEffect(() => {
      titleRef.current?.focus();
  }, []);

  const handleTitleChange = (val: string) => {
    const newNotepad = [...notepad];
    newNotepad[idx] = { ...note, title: val };
    setNotepad(newNotepad);
  };

  const handleDescriptionChange = (val: string) => {
    const newNotepad = [...notepad];
    newNotepad[idx] = { ...note, description: val };
    setNotepad(newNotepad);
  };

  return (
    <div className={styles.notepadContent__textareaSection}>
      <Input.TextArea
        rows={1}
        autoSize
        ref={titleRef}
        onPressEnter={(e) => {
          descriptionRef.current?.focus();
          e.preventDefault();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            if (e.currentTarget.selectionStart === e.currentTarget.value.length) {
              descriptionRef.current?.focus();
              e.preventDefault();
            }
          }
        }}
        value={note.title}
        onChange={(e) => handleTitleChange(e.target.value)}
        className={cx(styles.notepadContent__textarea_title, styles.notepadContent__textareaControl)}
        placeholder="Customer Name"
      />
      <Input.TextArea
        autoSize
        ref={descriptionRef}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' && e.currentTarget?.selectionStart === 0) {
            titleRef.current?.focus();
            e.preventDefault();
          }
        }}
        className={cx(styles.notepadContent__textarea_description, styles.notepadContent__textareaControl)}
        placeholder="Problems and other quick notes."
        value={note.description}
        onChange={(e) => handleDescriptionChange(e.target.value)}
      />
      <Button.$
        dataTestId="copy-notes"
        disabled={!note.description}
        onClick={() => {
          setNotes([...notes, note.description]);
        }}
        type={Button.Type.Link}
      >
        Add text to the Job Description
      </Button.$>
    </div>
  );
});

export const NotepadContent: FC = observer(() => {

  const { fieldsAmount, setFieldsAmount, setNotepad } = RootStore;

  useEffect(() => {
    if (fieldsAmount === 0) {
      setFieldsAmount(1);
    }
  }, [fieldsAmount, setFieldsAmount]);

  return (
    <div className={styles.notepadContent}>
      <div className={styles.notepadContent__title}>
        <div className={styles.notepadContent__titleText}></div>
        <Button.$
          type={Button.Type.Link}
          size={Button.Size.Small}
          className={styles.notepadContent__titleLink}
          dataTestId="clear-all-notepads"
          onClick={() => {
            setFieldsAmount(0);
            setNotepad([{ title: '', description: '' }]);
          }}
        >
          Clear All
        </Button.$>
      </div>
      <div className={styles.notepadContent__textarea}>
        {[...Array(fieldsAmount).keys()].map((i) => (
          <InputSection
            key={i}
            idx={i}
          />
        ))}
      </div>
      <Button.$
        type={Button.Type.Dashed}
        dataTestId="add-notepad-field"
        onClick={() => {
          setFieldsAmount(fieldsAmount + 1);
        }}
        className={styles.notepadContent__action}
      >
        + Add
      </Button.$>
    </div>
  );
});
